import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'components/Luxkit/Typography/Heading'
import cn from 'clsx'
import { rem } from 'polished'
import { bezierCurve } from 'styles/tools/constants'
import LayoutContainer from 'components/Common/LayoutContainer'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import zIndex from 'styles/tools/z-index'
import { setAppBannerCookie } from 'cookies/appBannerCookie'
import TextLink from 'components/Luxkit/TextLink'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import { useResponsive } from 'hooks/useResponsive'
import { fireInteractionEvent } from 'api/googleTagManager'
import getDatasetFromEvent from 'lib/getDatasetFromEvent'
import Backdrop from 'components/utils/Backdrop'
import Image from 'components/Common/Image'
import noop from 'lib/noop'
import config from 'constants/config'

interface Props {
  currentRegionCode: string;
  onClose?: () => void;
  hasCpcUtmMedium: Boolean;
  hasGoogleClickIdentifier: Boolean;
  regionPromotionData: App.BannerPromotionData;
}

const Root = styled.div`
  border-radius: ${props => props.theme.borderRadius.large} ${props => props.theme.borderRadius.large} 0 0;
  transform: translateY(100%);
  transition: transform 0.3s ${bezierCurve}, opacity 0.2s ${bezierCurve};
  position: fixed;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  box-shadow: ${({ theme }) => theme.shadow.top.large};
  width: 100%;
  bottom: 0;
  opacity: 0;
  pointer-events: none;
  z-index: ${zIndex.modal - 1};

  &.open {
    transform: translateY(0);
    pointer-events: auto;
    opacity: 1;
  }
`

const Inner = styled(LayoutContainer)`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(24)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: flex-end;
    gap: ${rem(32)};
    padding-left: ${rem(40)};
    padding-right: ${rem(40)};
  }
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: ${rem(8)};
  flex-direction: row-reverse;
  justify-content: flex-end;

  > * {
    flex-grow: 1;
  }
`

const HeadingWrapper = styled.div`
  display: flex;
  gap: ${rem(20)};

  ${mediaQueryDown.mobile} {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  & :first-child {
    flex-shrink: 0;
  }
`
const StyledBackdrop = styled(Backdrop)`
  z-index: ${zIndex.modal - 2} !important;
`

let circuitBreak = 0
function ContinueToAppDrawer({ regionPromotionData, onClose = noop, hasCpcUtmMedium, hasGoogleClickIdentifier }: Props) {
  const [url, setUrl] = useState<string>('')
  const [copy, setCopy] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setOpen(true)
        return window.removeEventListener('scroll', handleScroll)
      }
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const [match] = useResponsive() // This useResponsive is ok to use because result will be used inside an useEffect and never in the SSR

  const uptoTablet = useMemo(() => match.mobile || match.tablet, [match])

  useEffect(() => {
    // This is here because the AF_SMART_SCRIPT does not have a callback
    // So we need to try multiple times (5) to check if the script has loaded
    // Before we show the app banner
    const interval = setInterval(() => {
      // We only want to show this on upto tablet size
      if (!uptoTablet) {
        // So if screen size is larger we clear the interval
        !!interval && clearInterval(interval)
      }
      else if (!window.AF_SMART_SCRIPT_RESULT) {
        // We try every 5 seconds 5 times
        circuitBreak++
        // If past 5 times there is no script loaded we clear the interval
        if (circuitBreak > 4) clearInterval(interval)
      }
      else {
        // If the script is loaded and the screen size is upto tablet we show the banner by setting it all up
        // And then we clear the interval
        setUrl(window.AF_SMART_SCRIPT_RESULT.clickURL)
        setTimeout(() => {
          window.AF_SMART_SCRIPT.fireImpressionsLink()
        }, 1000)
        clearInterval(interval)
      }
    }, 5000)
    return () => {
      // Clear interval before the component unmounts to prevent any memory leaks
      !!interval && clearInterval(interval)
    }
  }, [uptoTablet])

  const closeAndSetCookie = useCallback((event) => {
    const action = getDatasetFromEvent(event, 'action')
    const label = getDatasetFromEvent(event, 'label')
    setOpen(false)
    onClose()
    setAppBannerCookie()
    fireInteractionEvent({
      // All the events
      // category: app-banner / action: exit / label: outside -  user has exited by clicking outside the banner
      // category: app-banner / action: exit / label: dismiss - user has dismissed by clicking not now
      // category: app-banner / action: switch / label: button - user has switched to the app by clicking the app button
      // category: app-banner / action: copy / label: promo - user has copied the promo code to the clip board
      category: 'app-banner',
      ...{ action, label },
    })
  }, [onClose])

  const copyToClipboard = useCallback((event) => {
    const code = getDatasetFromEvent(event, 'code')
    setCopy(true)
    copyValueToClipboard(code)
    setTimeout(() => {
      setCopy(false)
    }, 2000)
  }, [])

  const showSpecialCodeForAdClicks = !!regionPromotionData.specialCodeForAdClicks && (hasCpcUtmMedium || hasGoogleClickIdentifier)
  const code = showSpecialCodeForAdClicks ? regionPromotionData.specialCodeForAdClicks : regionPromotionData.code
  const { discount, minSpend } = regionPromotionData

  const genericAppDrawer = !(discount && code && minSpend)

  const validityText = config.BRAND === 'luxuryescapes' ?
    ' spend. Valid on all Limited Time LUX Exclusive, LUX Premium Collection and Ultra LUX offers. Terms & conditions apply' :
    ' spend. One use only, not in conjunction with any other offers. Valid on all Experiences, Limited Time Exclusive and Premium Collection offers, Tours, Cruises and Last Minute Escapes. Terms & conditions apply'

  return (
    <>
      <Root className={cn({ open: open && url })}>
        <Inner size="sm">
          <TextWrapper>
            <HeadingWrapper>
              <Image id={ config.appBanner.icon } height="40" width="40" />
              <Heading variant="heading5" as="h1">
                {genericAppDrawer && 'Experience our brand-new Luxury Escapes app'}
                {!genericAppDrawer && <>Get <FormatCurrency value={discount}/> off your next app booking with promo code: <TextLink data-action="copy" data-label="promo" data-code={code} onClick={copyToClipboard}>{code}</TextLink>{copy && <BodyText as="span" variant="small" colour="neutral-two">&nbsp;Copied</BodyText>}</>}
              </Heading>
            </HeadingWrapper>
            <BodyText variant={genericAppDrawer ? 'large' : 'small'} colour="neutral-two">
              {!genericAppDrawer && <> Minimum <FormatCurrency value={minSpend}/>{validityText}</>}
              {genericAppDrawer && "Your personalised guide to the world's best destinations and travel offers."}
            </BodyText>
          </TextWrapper>
          <ButtonsWrapper>
            <TextButton data-action="switch" data-label="button" href={url} onClick={closeAndSetCookie} kind="primary" size="large">Switch to the app</TextButton>
            <TextButton data-action="exit" data-label="dismiss" onClick={closeAndSetCookie} kind="secondary" size="large">Not now</TextButton>
          </ButtonsWrapper>
        </Inner>
      </Root>
      <StyledBackdrop data-action="exit" data-label="outside" onClick={closeAndSetCookie} visible={!!(open && url)} />
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  hasCpcUtmMedium: state.utm.medium === 'cpc',
  hasGoogleClickIdentifier: !!state.router.location.query.gclid,
})

export default connect(mapStateToProps)(ContinueToAppDrawer)
